import { useMemo } from "react";
import Layout from "@/components/Layout";
import { CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { articlesContent, articlesList } from "@/data/blogArticles";

interface AuditItem {
  page: string;
  path: string;
  title: string;
  hasMetaDesc: boolean;
  hasJsonLd: boolean;
  hasH1: boolean;
  issues: string[];
}

const pages: { page: string; path: string; title: string; hasMetaDesc: boolean; hasJsonLd: boolean; hasH1: boolean }[] = [
  { page: "Strona główna", path: "/", title: "Naklejki 3D – Naklejki zalewane żywicą | naklejki-3d.com.pl", hasMetaDesc: true, hasJsonLd: true, hasH1: true },
  { page: "Produkty", path: "/produkty", title: "Naklejki 3D Doming – Pełna Oferta", hasMetaDesc: true, hasJsonLd: true, hasH1: true },
  { page: "Zamówienie", path: "/zamowienie", title: "Zamów Darmową Wycenę", hasMetaDesc: true, hasJsonLd: false, hasH1: true },
  { page: "O nas", path: "/o-nas", title: "O nas – Dlaczego warto?", hasMetaDesc: true, hasJsonLd: false, hasH1: true },
  { page: "Blog", path: "/blog", title: "Blog & Porady", hasMetaDesc: true, hasJsonLd: false, hasH1: true },
  { page: "Kontakt", path: "/kontakt", title: "Kontakt", hasMetaDesc: true, hasJsonLd: true, hasH1: true },
];

const SeoAudit = () => {
  const auditData = useMemo<AuditItem[]>(() => {
    const items: AuditItem[] = pages.map((p) => {
      const issues: string[] = [];
      if (!p.hasMetaDesc) issues.push("Brak meta description");
      if (!p.hasJsonLd) issues.push("Brak danych strukturalnych JSON-LD");
      if (!p.hasH1) issues.push("Brak nagłówka H1");
      if (p.title.length > 60) issues.push(`Tytuł za długi (${p.title.length}/60 znaków)`);
      return { ...p, issues };
    });

    // Add blog articles
    articlesList.forEach((a) => {
      const article = articlesContent[a.slug];
      const issues: string[] = [];
      if (!article?.description) issues.push("Brak meta description");
      if (article?.title && article.title.length > 60) issues.push(`Tytuł za długi (${article.title.length}/60)`);
      items.push({
        page: `Blog: ${a.titleKey.slice(0, 40)}...`,
        path: `/blog/${a.slug}`,
        title: article?.title || a.titleKey,
        hasMetaDesc: !!article?.description,
        hasJsonLd: true,
        hasH1: true,
        issues,
      });
    });

    return items;
  }, []);

  const totalIssues = auditData.reduce((acc, item) => acc + item.issues.length, 0);
  const score = Math.round(((auditData.length * 3 - totalIssues) / (auditData.length * 3)) * 100);

  return (
    <Layout>
      <section className="gradient-navy py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-3xl lg:text-4xl font-bold text-primary-foreground mb-2">Panel SEO – Audyt strony</h1>
          <p className="text-primary-foreground/70">Automatyczny przegląd stanu SEO wszystkich podstron</p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-4 max-w-5xl">
          {/* Score */}
          <div className="text-center mb-10">
            <div className={`inline-flex items-center gap-3 px-8 py-4 rounded-xl border ${score >= 80 ? "border-green-300 bg-green-50" : score >= 50 ? "border-yellow-300 bg-yellow-50" : "border-red-300 bg-red-50"}`}>
              <span className="text-4xl font-bold">{score}%</span>
              <span className="text-muted-foreground text-sm text-left leading-tight">
                Wynik SEO<br />
                {totalIssues === 0 ? "Brak problemów!" : `${totalIssues} problem${totalIssues === 1 ? "" : totalIssues < 5 ? "y" : "ów"}`}
              </span>
            </div>
          </div>

          {/* Checklist globalna */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
            <CheckCard label="sitemap.xml" ok description="Mapa strony dostępna" />
            <CheckCard label="robots.txt" ok description="Plik robots.txt z Sitemap" />
            <CheckCard label="Canonical URL" ok description="Tagi canonical na wszystkich stronach" />
          </div>

          {/* Tabela audytu */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-secondary/50">
                    <th className="text-left p-4 font-semibold text-foreground">Strona</th>
                    <th className="text-center p-4 font-semibold text-foreground">Meta desc</th>
                    <th className="text-center p-4 font-semibold text-foreground">JSON-LD</th>
                    <th className="text-center p-4 font-semibold text-foreground">H1</th>
                    <th className="text-left p-4 font-semibold text-foreground">Problemy</th>
                  </tr>
                </thead>
                <tbody>
                  {auditData.map((item, i) => (
                    <tr key={i} className="border-b border-border last:border-0 hover:bg-secondary/30">
                      <td className="p-4">
                        <div className="font-medium text-foreground">{item.page}</div>
                        <div className="text-xs text-muted-foreground">{item.path}</div>
                      </td>
                      <td className="p-4 text-center">{item.hasMetaDesc ? <CheckCircle className="w-5 h-5 text-green-500 mx-auto" /> : <XCircle className="w-5 h-5 text-red-500 mx-auto" />}</td>
                      <td className="p-4 text-center">{item.hasJsonLd ? <CheckCircle className="w-5 h-5 text-green-500 mx-auto" /> : <XCircle className="w-5 h-5 text-red-500 mx-auto" />}</td>
                      <td className="p-4 text-center">{item.hasH1 ? <CheckCircle className="w-5 h-5 text-green-500 mx-auto" /> : <XCircle className="w-5 h-5 text-red-500 mx-auto" />}</td>
                      <td className="p-4">
                        {item.issues.length === 0 ? (
                          <span className="text-green-600 text-xs font-medium">OK</span>
                        ) : (
                          <div className="space-y-1">
                            {item.issues.map((issue, j) => (
                              <div key={j} className="flex items-center gap-1.5 text-xs text-amber-600">
                                <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                                {issue}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Rekomendacje */}
          <div className="mt-10 bg-card border border-border rounded-xl p-6">
            <h2 className="text-xl font-bold text-foreground mb-4">💡 Rekomendacje</h2>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>• Dodaj tagi <code className="text-foreground bg-secondary px-1.5 py-0.5 rounded text-xs">canonical</code> na każdej podstronie aby uniknąć duplikatów</li>
              <li>• Rozważ dodanie JSON-LD na stronach: Zamówienie, O nas, Blog</li>
              <li>• Publikuj nowe artykuły blogowe co 1-2 tygodnie dla lepszej widoczności</li>
              <li>• Optymalizuj zdjęcia (WebP, lazy loading) dla lepszego Core Web Vitals</li>
              <li>• Dodaj alt text do wszystkich obrazków</li>
              <li>• Użyj generatora AI artykułów do tworzenia nowych treści SEO</li>
            </ul>
          </div>
        </div>
      </section>
    </Layout>
  );
};

const CheckCard = ({ label, ok, description }: { label: string; ok: boolean; description: string }) => (
  <div className={`p-4 rounded-xl border ${ok ? "border-green-200 bg-green-50" : "border-amber-200 bg-amber-50"} flex items-start gap-3`}>
    {ok ? <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" /> : <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />}
    <div>
      <div className="font-semibold text-foreground text-sm">{label}</div>
      <div className="text-xs text-muted-foreground">{description}</div>
    </div>
  </div>
);

export default SeoAudit;
